import { supabase } from "@/lib/supabase";

const BUCKET = "private";

export interface RecorderFormat {
  mimeType: string;
  extension: string;
}

const CANDIDATE_FORMATS: RecorderFormat[] = [
  { mimeType: "video/webm;codecs=vp9,opus", extension: "webm" },
  { mimeType: "video/webm;codecs=vp8,opus", extension: "webm" },
  { mimeType: "video/webm", extension: "webm" },
  { mimeType: "video/mp4", extension: "mp4" },
];

/** Pick the best video container the current browser can actually record. */
export function pickRecorderFormat(): RecorderFormat {
  if (typeof MediaRecorder === "undefined") return CANDIDATE_FORMATS[CANDIDATE_FORMATS.length - 1];
  const supported = CANDIDATE_FORMATS.find((format) => MediaRecorder.isTypeSupported(format.mimeType));
  return supported ?? { mimeType: "", extension: "webm" };
}

/**
 * Upload a recorded walkaround clip to Backend Storage (private bucket) and
 * return the stored object path so we can mint a signed URL for playback later.
 */
export async function uploadCheckinVideo(
  bookingId: number,
  phase: string,
  blob: Blob,
  mimeType: string
): Promise<{ path: string; size: number }> {
  const safePhase = (phase || "pickup").toLowerCase().replace(/[^a-z0-9-]/g, "");
  const extension = mimeType.includes("mp4") ? "mp4" : "webm";
  const path = `checkins/${bookingId}/${safePhase}-${Date.now()}.${extension}`;

  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, {
    contentType: mimeType || "video/webm",
    upsert: false,
  });
  if (error) throw error;

  return { path, size: blob.size };
}

/** Create a short-lived signed URL for a stored walkaround clip. */
export async function signedCheckinUrl(path: string, expiresIn = 3600): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, expiresIn);
  if (error) throw error;
  if (!data?.signedUrl) throw new Error("This clip isn't available right now.");
  return data.signedUrl;
}

/** Grab a small JPEG still from a live video element (used for thumbnails + AI). */
export function captureThumbnail(video: HTMLVideoElement | null, maxWidth = 480): string | null {
  if (!video || !video.videoWidth || !video.videoHeight) return null;
  try {
    const scale = Math.min(1, maxWidth / video.videoWidth);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.6);
  } catch {
    return null;
  }
}